import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import type { CalendarEvent, Me, Quote, TodayPlan, TodayQuotes } from '../api/types';
import { PRACTICE_TYPE_ICONS } from '../api/types';
import { MascotWelcome, type GreetingTodayStatus } from '../components/MascotWelcome';
import {
  formatDate,
  formatMinutesShort,
  formatNumber,
  formatPrice,
  languageName,
  languageNameIn,
  localizedPlanText,
  parseIsoDate,
  practiceTypeLabel,
  traditionLabel,
  useJourneyLocale,
} from '../i18n/journey';
import { useUser } from '../state/UserContext';

// Same key the old blocking popup used, so anyone who already dismissed it today isn't re-greeted.
const WELCOME_DISMISSED_KEY = 'omnist.welcomeShownDate';

function localIsoDate(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type Loadable<T> = { status: 'loading' } | { status: 'error'; message: string | null } | { status: 'ready'; data: T };

/** One independently-loaded Home section (review fix 2026-09-10): each has its own loading,
 * error and retry state, so a failed quote can never remove the Begin action and retrying one
 * section never discards another's already-loaded content. */
function useLoadable<T>(fetcher: () => Promise<T>): [Loadable<T>, () => void] {
  const [state, setState] = useState<Loadable<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetcher().then(
      (data) => {
        if (!cancelled) setState({ status: 'ready', data });
      },
      (e: unknown) => {
        if (!cancelled) setState({ status: 'error', message: e instanceof Error ? e.message : null });
      },
    );
    return () => {
      cancelled = true;
    };
    // `fetcher` is intentionally not a dependency: a section reloads only on retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);
  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return [state, retry];
}

function TodayCard({
  state,
  retry,
  locale,
  hasTraditions,
}: {
  state: Loadable<TodayPlan>;
  retry: () => void;
  locale: string;
  hasTraditions: boolean;
}) {
  const { t, lang } = useJourneyLocale();

  if (state.status === 'loading') {
    return (
      <section className="themed-card today-card today-card-loading" aria-busy="true" aria-label={t('home.today.ariaLabel')}>
        <div className="spinner" />
        <p className="page-subtitle">{t('home.today.finding')}</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="themed-card today-card" aria-label={t('home.today.ariaLabel')}>
        <span className="today-card-label">{t('home.reason.none')}</span>
        <p className="today-card-explanation" role="alert">
          {t('home.today.loadError', { message: state.message ?? t('shared.somethingWentWrong') })}
        </p>
        <div className="today-card-actions">
          <button type="button" className="btn btn-primary" onClick={retry}>
            {t('shared.tryAgain')}
          </button>
          <Link to="/practices" className="btn btn-secondary">
            {t('home.today.explore')}
          </Link>
        </div>
      </section>
    );
  }

  const plan = state.data;
  const practice = plan.practice ?? null;
  const planText = localizedPlanText(plan, t, lang, hasTraditions);

  if (!plan.practiceId) {
    return (
      <section className="themed-card today-card" aria-label={t('home.today.ariaLabel')}>
        <span className="today-card-label">{t('home.reason.none')}</span>
        <p className="today-card-explanation">{planText.explanation}</p>
        <p className="today-card-next">{planText.nextStep}</p>
        <div className="today-card-actions">
          <Link to="/practices" className="btn btn-primary">
            {t('home.today.explore')}
          </Link>
          <Link to="/settings" className="btn btn-secondary">
            {t('home.today.changeTraditions')}
          </Link>
        </div>
      </section>
    );
  }

  if (!practice) {
    // An older server without the `practice` summary: still usable -- the practice page itself
    // shows the right access state.
    return (
      <section className="themed-card today-card" aria-label={t('home.today.ariaLabel')}>
        <span className="today-card-label">{t(`home.reason.${plan.reason}`)}</span>
        <p className="today-card-explanation">{planText.explanation}</p>
        <div className="today-card-actions">
          <Link to={`/practice/${plan.practiceId}`} className="btn btn-primary">
            {t('home.today.open')}
          </Link>
          <Link to="/practices" className="btn btn-ghost">
            {t('home.today.chooseElse')}
          </Link>
        </div>
      </section>
    );
  }

  const languageNameText = languageName(practice.language);
  const requestedName = languageNameIn(lang, locale);
  const isAvailable = practice.access === 'available';

  return (
    <section className="themed-card today-card today-card-dominant" aria-label={t('home.today.ariaLabel')}>
      <span className="today-card-label">{t(`home.reason.${plan.reason}`)}</span>
      <h2 className="today-card-title" lang={practice.languageFallback ? practice.language : undefined}>
        <span aria-hidden="true">{PRACTICE_TYPE_ICONS[practice.type]}</span> {practice.title}
      </h2>
      <p className="today-card-meta">
        <span>{traditionLabel(t, practice.tradition)}</span>
        <span>{practiceTypeLabel(t, practice.type)}</span>
        <span>{formatMinutesShort(lang, practice.durationSeconds)}</span>
        <span className="today-lang-badge" title={t('home.today.languageBadgeTitle')}>
          <span lang={practice.language}>{languageNameText}</span>
          {practice.languageFallback && <> &middot; {t('home.today.notTranslated', { language: requestedName })}</>}
        </span>
      </p>
      <p className="today-card-why">
        <span className="today-card-why-label">{t('home.today.why')}</span> {planText.explanation}
      </p>

      {practice.access === 'premium_required' && (
        <p className="today-card-next">{t('home.today.premiumNote', { price: formatPrice(lang) })}</p>
      )}
      {practice.access === 'prerequisite_required' && (
        <p className="today-card-next">{t('home.today.prerequisiteNote')}</p>
      )}
      {practice.access === 'unpublished' && <p className="today-card-next">{t('home.today.unpublishedNote')}</p>}

      <div className="today-card-actions">
        {isAvailable ? (
          <Link to={`/practice/${practice.practiceId}`} className="btn btn-primary today-begin">
            {t('home.today.begin')}
          </Link>
        ) : practice.access === 'premium_required' ? (
          <Link to="/settings?tab=billing" className="btn btn-primary">
            {t('home.today.seePremium')}
          </Link>
        ) : null}
        <Link to="/practices" className="btn btn-ghost">
          {t('home.today.chooseElse')}
        </Link>
      </div>
    </section>
  );
}

/** Today's quotes, one at a time with previous/next -- a stack of three full cards was most of
 * Home's scroll height (layout pass 2026-09-10). */
function QuotesSection({
  state,
  retry,
  primaryTradition,
}: {
  state: Loadable<TodayQuotes>;
  retry: () => void;
  primaryTradition: string | null;
}) {
  const { t, lang } = useJourneyLocale();
  const [index, setIndex] = useState(0);
  if (state.status === 'loading') {
    return <p className="page-subtitle home-section-status">{t('home.quotes.loading')}</p>;
  }
  if (state.status === 'error') {
    return (
      <p className="home-section-status">
        {t('home.quotes.error')}{' '}
        <button type="button" className="btn-inline-link" onClick={retry}>
          {t('shared.tryAgain')}
        </button>
      </p>
    );
  }
  // Feature the user's chosen main practice's quote first, when they have one.
  const quotes: Quote[] = primaryTradition
    ? [...state.data.quotes].sort((a, b) =>
        a.tradition === primaryTradition ? -1 : b.tradition === primaryTradition ? 1 : 0,
      )
    : state.data.quotes;
  if (quotes.length === 0) return null;
  const current = Math.min(index, quotes.length - 1);
  const q = quotes[current];
  const step = (delta: number) => setIndex((i) => (Math.min(i, quotes.length - 1) + delta + quotes.length) % quotes.length);
  return (
    <figure className="themed-card quote-card">
      <div className="quote-card-body" aria-live="polite">
        <blockquote className="quote-block">
          <p className="quote-text">&ldquo;{q.text}&rdquo;</p>
        </blockquote>
        <figcaption className="quote-caption">
          <span className="quote-author">&mdash; {q.author}</span>
          <span className="quote-tradition-tag">{traditionLabel(t, q.tradition)}</span>
        </figcaption>
      </div>
      {quotes.length > 1 && (
        <div className="quote-nav">
          <button type="button" className="quote-nav-btn" onClick={() => step(-1)} aria-label={t('home.quotes.previous')}>
            <span aria-hidden="true">&lsaquo;</span>
          </button>
          <span className="quote-nav-position">
            {t('home.quotes.position', { current: formatNumber(lang, current + 1), total: formatNumber(lang, quotes.length) })}
          </span>
          <button type="button" className="quote-nav-btn" onClick={() => step(1)} aria-label={t('home.quotes.next')}>
            <span aria-hidden="true">&rsaquo;</span>
          </button>
        </div>
      )}
    </figure>
  );
}

/** Practice history at a glance -- secondary to the practice itself, so it lives in the side
 * column on wide screens and is hidden on phones (the top bar already shows the streak). */
function RhythmCard({ me }: { me: Me }) {
  const { t, lang } = useJourneyLocale();
  const stats = [
    { key: 'current', label: t('streak.current'), value: me.currentStreak },
    { key: 'longest', label: t('streak.longest'), value: me.longestStreak },
    { key: 'days', label: t('home.rhythm.practiceDays'), value: me.totalPracticeDays },
  ];
  return (
    <section className="home-rhythm" aria-labelledby="home-rhythm-heading">
      <h2 id="home-rhythm-heading" className="section-title">{t('home.rhythm.heading')}</h2>
      <dl className="rhythm-stats">
        {stats.map((s) => (
          <div key={s.key} className="rhythm-stat">
            <dt>{s.label}</dt>
            <dd>{formatNumber(lang, s.value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function ComingUp({ state, retry }: { state: Loadable<CalendarEvent[]>; retry: () => void }) {
  const { t, lang } = useJourneyLocale();
  if (state.status === 'loading') return null;
  if (state.status === 'error') {
    return (
      <p className="home-section-status">
        {t('home.comingUp.error')}{' '}
        <button type="button" className="btn-inline-link" onClick={retry}>
          {t('shared.tryAgain')}
        </button>
      </p>
    );
  }
  const today = localIsoDate();
  const next = [...state.data].filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date))[0];
  if (!next) {
    return (
      <p className="home-section-status">
        {t('home.comingUp.empty')} <Link to="/calendar">{t('home.comingUp.openCalendar')}</Link>
      </p>
    );
  }
  const nextDate = parseIsoDate(next.date);
  const date = formatDate(lang, nextDate, { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <div className="themed-card coming-up-card">
      <div className="coming-up-date" aria-hidden="true">
        <span className="coming-up-date-day">{formatDate(lang, nextDate, { day: 'numeric' })}</span>
        <span className="coming-up-date-month">{formatDate(lang, nextDate, { month: 'short' })}</span>
      </div>
      <div className="coming-up-body">
        <span className="today-card-label">
          {t('home.comingUp.label')} &middot; {date}
        </span>
        <p className="coming-up-title">{next.title}</p>
        <p className="coming-up-meta">
          {traditionLabel(t, next.tradition)}
          {(next.movable || next.uncertainty === 'estimated' || next.community) &&
            ` · ${t('home.comingUp.datesDiffer')}`}
        </p>
        <Link to="/calendar" className="btn-inline-link">
          {t('home.comingUp.seeCalendar')}
        </Link>
      </div>
    </div>
  );
}

export function Home() {
  const { me } = useUser();
  const { t, lang } = useJourneyLocale();
  // Independent failure domains -- deliberately NOT Promise.all (review finding 9).
  const [today, retryToday] = useLoadable(() => api.getToday());
  const [quotes, retryQuotes] = useLoadable(() => api.getTodayQuotes());
  const [calendar, retryCalendar] = useLoadable(() => api.getCalendar().then((r) => r.events));
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(WELCOME_DISMISSED_KEY) !== localIsoDate()) setShowWelcome(true);
    } catch {
      // localStorage unavailable -- the greeting is optional, so just skip it.
    }
  }, []);

  function dismissWelcome() {
    setShowWelcome(false);
    try {
      localStorage.setItem(WELCOME_DISMISSED_KEY, localIsoDate());
    } catch {
      // ignore
    }
  }

  const greetingStatus: GreetingTodayStatus =
    today.status === 'loading'
      ? 'loading'
      : today.status === 'error'
        ? 'error'
        : today.data.practice?.access === 'available' || (today.data.practiceId && !today.data.practice)
          ? 'practice'
          : 'empty';

  return (
    <div className="home-page">
      <h1 className="page-title">{t('home.title')}</h1>
      <p className="page-subtitle">
        {formatDate(lang, new Date(), { weekday: 'long', month: 'long', day: 'numeric' })}
      </p>

      {showWelcome && me && (
        <MascotWelcome
          gender={me.gender}
          displayName={me.displayName || t('home.friend')}
          currentStreak={me.currentStreak}
          primaryTradition={me.primaryTradition}
          todayStatus={greetingStatus}
          onDismiss={dismissWelcome}
        />
      )}

      {/* Wide screens: Today + Coming up on the left, rhythm + quote in a side column. Phones keep
          the review's order -- Today first, then the quote, then the calendar moment. */}
      <div className="home-layout">
        <div className="home-today">
          <TodayCard
            state={today}
            retry={retryToday}
            locale={me?.locale ?? lang}
            hasTraditions={(me?.traditions.length ?? 0) > 0}
          />
        </div>

        <div className="home-aside">
          {me && <RhythmCard me={me} />}
          <h2 className="section-title">{t('home.quoteHeading')}</h2>
          <QuotesSection state={quotes} retry={retryQuotes} primaryTradition={me?.primaryTradition ?? null} />
        </div>

        <div className="home-coming">
          <h2 className="section-title">{t('home.calendarHeading')}</h2>
          <ComingUp state={calendar} retry={retryCalendar} />
        </div>
      </div>
    </div>
  );
}
